import { withStyles } from "@mui/styles";
import PropTypes from "prop-types";
import React from "react";

import {
  COLOR_KICK_LIGHT_YELLOW,
  COLOR_KICK_NAVY,
} from "~/shared/sharedConstants.js";

// A light-yellow note under an intake question for a warning the patient can
// act on right away (currently the blood-pressure mix-up check, see
// utils/bloodPressureCheck.js). Same surface tokens as AiCoachCallout's
// suggestion state — navy text on light yellow (#1e2347 on #fff7e5 ≈ 14:1)
// behind a thin orange rule — so the two read as one family, but with no
// typewriter reveal: this is a plain validation message, not a coach, and it
// must be readable the instant it appears.
//
// `text` is one paragraph or a list of them; a paragraph is a string or a list
// of runs, where a run is a string, `{ strong: text }` for emphasis (the
// blood-pressure note bolds the two answers it quotes), or `{ action: label }`.
// The action run is rendered as a real button styled as an inline link — the
// one thing the note tells the patient to do, sitting in the sentence that
// tells them to do it ("…, or [change your top number]."). It is
// `display: inline` so a multi-word label wraps like the surrounding text.
const styles = {
  note: {
    marginTop: "20px",
    padding: "14px 18px",
    borderLeft: "2px solid #F0A97F",
    borderRadius: "0 8px 8px 0",
    background: COLOR_KICK_LIGHT_YELLOW,
    maxWidth: "58ch",
    textAlign: "left",
    animation: "$noteRise .26s cubic-bezier(.2,.7,.3,1) both",
  },
  body: {
    margin: 0,
    fontSize: "15px",
    lineHeight: 1.58,
    color: COLOR_KICK_NAVY,
    "& + &": { marginTop: "10px" },
  },
  // Explicit weight, because a bare <strong> renders unchanged here: the
  // bootstrap reset styles it `font-weight: bolder`, which is relative, and
  // the page text inherits 300 (shared/styles/baseStyles.js defaultFont), so
  // "bolder" resolves to 400 — regular. 700 is synthesized (the Regular face
  // has no bold cut) and visibly bold.
  strong: {
    fontWeight: "bold",
  },
  action: {
    display: "inline",
    margin: 0,
    padding: 0,
    border: 0,
    background: "none",
    font: "inherit",
    fontWeight: "bold",
    color: COLOR_KICK_NAVY,
    textDecoration: "underline",
    cursor: "pointer",
  },
  "@keyframes noteRise": {
    from: { opacity: 0, transform: "translateY(-5px)" },
    to: { opacity: 1, transform: "none" },
  },
  "@media (prefers-reduced-motion: reduce)": {
    note: { animation: "none" },
  },
};

function IntakeWarningNote({ classes, text, onAction }) {
  const renderRun = (run, key) => {
    if (typeof run === "string") return run;
    if (run.strong !== undefined) {
      return (
        <strong key={key} className={classes.strong}>
          {run.strong}
        </strong>
      );
    }
    // Without a handler the label still reads as part of the sentence.
    if (!onAction) return run.action;
    return (
      <button
        key={key}
        type="button"
        className={classes.action}
        onClick={onAction}
      >
        {run.action}
      </button>
    );
  };

  return (
    <div className={classes.note} role="alert">
      {[].concat(text).map((paragraph, i) => (
        <p key={i} className={classes.body}>
          {[].concat(paragraph).map(renderRun)}
        </p>
      ))}
    </div>
  );
}

const runType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.exact({ strong: PropTypes.string.isRequired }),
  PropTypes.exact({ action: PropTypes.string.isRequired }),
]);
const paragraphType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.arrayOf(runType),
]);

IntakeWarningNote.propTypes = {
  classes: PropTypes.object.isRequired,
  text: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(paragraphType),
  ]).isRequired,
  // Fired by the `{ action }` run, if the text has one.
  onAction: PropTypes.func,
};

export default withStyles(styles)(IntakeWarningNote);
